'use client';

import { useEffect, useRef, useState } from 'react';
import { parseLines, wrapText } from '@/components/tools/canvas-mockup';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type CommentVariant = 'instagram-comment-generator' | 'youtube-comment-generator';

const theme: Record<
  CommentVariant,
  {
    badge: string;
    title: string;
    bg: string;
    text: string;
    meta: string;
    // Instagram shows the handle inline with the comment; YouTube puts it on its own line
    // above the text, with the like count under it.
    layout: 'inline' | 'stacked';
    meName: string;
    themName: string;
    avatarMe: string;
    avatarThem: string;
  }
> = {
  'instagram-comment-generator': {
    badge: 'Instagram',
    title: 'Instagram comment generator',
    bg: '#ffffff',
    text: '#000000',
    meta: '#8e8e8e',
    layout: 'inline',
    meName: 'you',
    themName: 'friend',
    avatarMe: '#c13584',
    avatarThem: '#405de6',
  },
  'youtube-comment-generator': {
    badge: 'YouTube',
    title: 'YouTube comment generator',
    bg: '#ffffff',
    text: '#0f0f0f',
    meta: '#606060',
    layout: 'stacked',
    meName: '@you',
    themName: '@viewer',
    avatarMe: '#ff0000',
    avatarThem: '#065fd4',
  },
};

export default function BrowserCommentGenerator({ variant }: { variant: CommentVariant }) {
  const t = theme[variant];
  const [comments, setComments] = useState(
    'This tool is exactly what I needed\n> Glad it helped, it all runs in the browser\nNo upload? that is the best part',
  );
  const [likes, setLikes] = useState('12');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = 2;
    const width = 480;
    const pad = 16;
    const avatar = t.layout === 'stacked' ? 40 : 34;
    const textX = pad + avatar + 12;
    const textWidth = width - textX - pad;
    const lineHeight = 22;
    const bodyFont = '400 16px system-ui, sans-serif';
    const nameFont = '600 15px system-ui, sans-serif';

    const parsed = parseLines(comments);

    ctx.font = bodyFont;
    const rows = parsed.map((c) => {
      const name = c.me ? t.meName : t.themName;
      // Inline layout puts the handle and the first words on the same line, so the first
      // line has less room for the comment itself.
      ctx.font = nameFont;
      const nameWidth = ctx.measureText(`${name} `).width;
      ctx.font = bodyFont;
      const lines =
        t.layout === 'inline'
          ? wrapText(ctx, c.text, textWidth - nameWidth)
          : wrapText(ctx, c.text, textWidth);
      const h =
        t.layout === 'inline'
          ? Math.max(avatar, lines.length * lineHeight) + 18
          : 20 + lines.length * lineHeight + 22;
      return { ...c, name, nameWidth, lines, h };
    });

    const totalHeight = pad + rows.reduce((sum, r) => sum + r.h, 0) + pad;
    canvas.width = width * scale;
    canvas.height = totalHeight * scale;
    ctx.scale(scale, scale);

    ctx.fillStyle = t.bg;
    ctx.fillRect(0, 0, width, totalHeight);

    let y = pad;
    for (const r of rows) {
      ctx.fillStyle = r.me ? t.avatarMe : t.avatarThem;
      ctx.beginPath();
      ctx.arc(pad + avatar / 2, y + avatar / 2, avatar / 2, 0, Math.PI * 2);
      ctx.fill();

      if (t.layout === 'inline') {
        ctx.font = nameFont;
        ctx.fillStyle = t.text;
        ctx.fillText(r.name, textX, y + 16);
        ctx.font = bodyFont;
        let ty = y + 16;
        r.lines.forEach((line, i) => {
          ctx.fillText(line, i === 0 ? textX + r.nameWidth : textX, ty);
          ty += lineHeight;
        });
        ctx.fillStyle = t.meta;
        ctx.font = '400 13px system-ui, sans-serif';
        ctx.fillText(`${likes} likes`, textX, ty);
      } else {
        ctx.font = nameFont;
        ctx.fillStyle = t.text;
        ctx.fillText(r.name, textX, y + 15);
        ctx.font = bodyFont;
        let ty = y + 15 + lineHeight;
        for (const line of r.lines) {
          ctx.fillText(line, textX, ty);
          ty += lineHeight;
        }
        ctx.fillStyle = t.meta;
        ctx.font = '400 13px system-ui, sans-serif';
        ctx.fillText(`${likes} likes    Reply`, textX, ty + 2);
      }

      y += r.h;
    }

    canvas.toBlob((blob) => {
      if (blob) setDownloadUrl(URL.createObjectURL(blob));
    }, 'image/png');
  }, [comments, likes, t]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{t.badge}</span>
        <h2 className="tool-live-title">{t.title}</h2>
        <p className="tool-live-subtitle">
          Write mock comments and download them as an image. Rendered entirely in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Comments (start a line with &gt; for your own)</span>
            <textarea onChange={(e) => setComments(e.target.value)} rows={7} value={comments} />
          </label>
          <label className="tool-live-field">
            <span>Like count</span>
            <input onChange={(e) => setLikes(e.target.value)} type="text" value={likes} />
          </label>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${variant}.png`}
                href={downloadUrl}
                // Fire-and-forget analytics only; see the same note in BrowserChatGenerator.tsx.
                onClick={() => {
                  void consumeToolUsage(variant, '').catch(() => {});
                }}
              >
                Download PNG
              </a>
            ) : null}
          </div>
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          <p className="tool-live-panel-note">
            Rendered locally with an HTML canvas. This is a mockup for design and fun.
          </p>
        </section>
      </div>
    </div>
  );
}
